import { Box, Button, Group, Stack, Text, Title, Badge } from "@mantine/core";
import { Link } from "react-router-dom";
import type { Release } from "@/content/types";
import { MediaImage } from "@/components/common/MediaImage";
import { formatDate } from "@/lib/format";

interface Props {
  release: Release;
}

export function SpotlightCard({ release }: Props) {
  const isPreRelease = release.status === "pre-release";
  const isAnnounced = release.status === "announced";

  let dateLine: string;
  if (isPreRelease && release.releaseDate) {
    dateLine = `${release.label} · Coming ${formatDate(release.releaseDate)}`;
  } else if (isAnnounced) {
    dateLine = "Release date TBA";
  } else {
    dateLine = release.label;
  }

  return (
    <Box
      p={{ base: "md", md: "xl" }}
      style={{
        background: "linear-gradient(135deg, var(--color-deep) 0%, var(--color-deeper) 100%)",
        border: "1px solid var(--color-ocean)",
        borderRadius: 16,
      }}
    >
      <Group align="flex-start" wrap="wrap" gap="xl">
        <Box style={{ flex: "0 0 280px", maxWidth: "100%" }}>
          <MediaImage
            image={release.cover}
            loading="eager"
            sizes="(min-width: 768px) 280px, 100vw"
            style={{ width: "100%", height: "auto", borderRadius: 12 }}
          />
        </Box>
        <Stack gap="sm" style={{ flex: "1 1 320px", minWidth: 0 }}>
          <Badge color={isPreRelease ? "magenta" : "indigo"} variant="light" size="sm">
            {isPreRelease ? "Pre-release" : isAnnounced ? "Upcoming" : release.status}
          </Badge>
          <Title order={1} size="h2" className="iridescent-text">
            {release.title}
          </Title>
          <Text size="sm" c="dimmed">
            {dateLine}
          </Text>
          {release.description && (
            <Text size="sm" c="var(--color-foam)" lineClamp={4}>
              {release.description}
            </Text>
          )}
          <Group mt="sm">
            <Button component={Link} to={`/releases/${release.slug}`} variant="filled" color="indigo">
              Listen
            </Button>
            <Button component={Link} to="/access" variant="outline" color="gray">
              Label / press access
            </Button>
          </Group>
        </Stack>
      </Group>
    </Box>
  );
}
